<template lang="pug">
v-dialog(:value="data.active", @input="close", max-width="600px")
  v-card.pt-3
    v-card-title
      span.headline Carga horária máxima de trabalho
    v-card-text
      v-form(ref="form", @submit.prevent="submit", lazy-validation)
        sos-select(
          label="APLICADA A(O)"
          :default="level"
          :items="levels"
          @sosSelectChanged="setLevel"
          defaultEmpty="Selecione um nível"
        ).mb-3
        .fieldError(v-if="touched && !level").mb-3
          | Você deve informar o nível de aplicação

        sos-select(
          v-if="level === 'user' || level === 'scale'",
          label="ESCALA"
          :default="scale_id"
          :items="scalesList"
          @sosSelectChanged="setScale"
          defaultEmpty="Selecione uma escala"
        ).mb-3
        .fieldError(v-if="(level === 'user' || level === 'scale') && touched && !scale_id").mb-3
          | Você deve informar a escala
        sos-select(
          v-if="level === 'user'"
          label="USUÁRIO"
          :default="user_id"
          :items="usersList"
          @sosSelectChanged="setUser"
          defaultEmpty="Selecione um usuário"
        ).mb-3
        .fieldError(v-if="level === 'user' && touched && !user_id").mb-3
          | Você deve informar o usuário
        sos-select(
          label="TIPO DE REGRA"
          :items="ruleTypes"
          :default="type"
          @sosSelectChanged="setType"
          defaultEmpty="Selecione um tipo de regra"
        ).mb-3
        .fieldError(v-if="touched && !type").mb-3
          | Você deve informar o tipo de regra
        .caption.grey--text.text--darken-1.mb-3(v-if="level === 'user' && ['daily_max_hours','weekly_max_hours','monthly_max_hours'].includes(type)")
          | Este limite considera apenas os plantões do usuário nesta escala. Para limitar a jornada somando todas as escalas do grupo, use uma regra de Grupo.
        .caption.grey--text.text--darken-1.mb-3(v-if="level === 'user' && ['min_hours_between_shifts','max_consecutive_work_days'].includes(type)")
          | Este limite considera todos os plantões do usuário, em qualquer escala do grupo.
        v-text-field(
          v-model.number="value",
          :label="getValueLabel(type)",
          type="number",
        )
        .fieldError(v-if="touched && !value").mb-3
          | Você deve informar um valor maior que zero
        sos-select(
          label="AÇÃO EM CASO DE VIOLAÇÃO"
          :default="action"
          :items="ruleActions"
          @sosSelectChanged="setAction"
          defaultEmpty="Selecione uma ação"
        ).mt-3
        .fieldError(v-if="touched && !action").mt-3
          | Você deve informar a ação
    v-card-actions.pb-5
      v-spacer
      sos-button.mr-2(
        @buttonClick="close",
        color="danger",
        buttonLabel="Cancelar",
        size="medium",
        :disabled="loading"
      )
      sos-button(
        @buttonClick="submit",
        color="success",
        :buttonLabel="loading ? 'Salvando...' : 'Salvar'",
        size="medium",
        :disabled="loading"
      )
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import SosButton from "@/components/SosButton";
import * as types from "@/store/working-hours-rules/types";
import MessageModal from "./MessageModal.vue";
import SosSelect from "@/components/SosSelect";
import {
  LEVELS,
  RULE_TYPE_MAX_CONSECUTIVE_WORK_DAYS,
  WORKING_HOUR_ACTIONS,
} from "../constants/workRules";

export default {
  name: "WorkingHoursRulesModal",
  components: {
    "sos-select": SosSelect,
    "sos-button": SosButton,
    MessageModal,
  },
  props: {
    active: Boolean,
    data: Object,
  },
  data() {
    return {
      level: "",
      type: "",
      value: "",
      action: "",
      entity_id: undefined,
      scale_id: "",
      user_id: "",
      touched: false,
      loading: false,
      existingRule: null,
      ruleId: null,
    };
  },
  computed: {
    ...mapState({
      scalesList: (state) =>
        state.app.groupScales.map((item) => ({
          key: item.scaleID,
          value: item.scaleID,
          text: item.scaleName,
        })),
      usersList: (state) =>
        state.app.groupUsers.map((item) => ({
          key: item.userID,
          value: item.userID,
          text: item.userName,
        })),
    }),
    ...mapGetters({
      RULE_TYPES: types.RULE_TYPES,
      RULES: types.RULES,
    }),
    levels() {
      return (
        LEVELS.map((level) => ({
          key: level.value,
          ...level,
        })) || []
      );
    },
    ruleTypes() {
      return (
        this.RULE_TYPES.map((type) => ({
          key: type.value,
          ...type,
        })) || []
      );
    },
    ruleActions() {
      return (
        WORKING_HOUR_ACTIONS.map((action) => ({
          key: action.value,
          ...action,
        })) || []
      );
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newVal) {
        this.buildWorkingHoursRuleData();
      },
    },
  },
  mounted() {
    this.resetForm();
  },
  methods: {
    ...mapActions({
      saveRule: types.SAVE_RULE,
      getRules: types.GET_RULES_BY_GROUP_ID,
    }),
    setLevel(optionValue, optionText, optionPayload) {
      this.level = optionValue;
    },
    setType(optionValue, optionText, optionPayload) {
      this.type = optionValue;
    },
    setAction(optionValue, optionText, optionPayload) {
      this.action = optionValue;
    },
    setScale(optionValue, optionText, optionPayload) {
      this.scale_id = optionValue;
      this.resetForm();
    },
    setUser(optionValue, optionText, optionPayload) {
      this.user_id = optionValue;
      this.resetForm();
    },
    buildWorkingHoursRuleData() {
      this.resetForm();
      const { id, level, type, value, action, scaleId, entityId } = this.data || {};
      this.ruleId = id || null;
      if (level) {
        this.level = level;
      }
      if (type) {
        this.type = type;
      }
      if (entityId && level === "user") {
        this.user_id = entityId;
      }
      if (entityId && level === "scale") {
        this.scale_id = entityId;
      } else if (scaleId) {
        this.scale_id = scaleId;
      }
      if (value) {
        this.value = value;
      }
      if (action) {
        this.action = action;
      }
    },
    getValueLabel(type) {
      switch (type) {
        case RULE_TYPE_MAX_CONSECUTIVE_WORK_DAYS.value:
          return "Quantidade de dias";
        default:
          return "Quantidade de horas";
      }
    },
    close() {
      this.$emit("close");
      if (this.$refs.form?.reset) {
        this.$refs.form.reset();
        this.$refs.form.resetValidation?.();
      }
      this.level = this.data.level || null;
      this.type = null;
      this.value = null;
      this.action = null;
      this.scale_id = null;
      this.ruleId = null;
    },
    submit() {
      this.loading = true;
      this.touched = true;
      if (!this.formIsValid()) {
        this.loading = false;
        return;
      }
      if (this.level === "user") {
        this.entity_id = this.user_id;
      } else if (this.level === "scale") {
        this.entity_id = this.scale_id;
      } else {
        this.entity_id = this.data.groupId;
      }
      const { level, type, value, action, scale_id, user_id, entity_id, ruleId } = this;
      this.saveRule({
        id: ruleId,
        level,
        type,
        value,
        action,
        scale_id: this.level === "user" ? scale_id : null,
        user_id: user_id || null,
        entity_id,
        group_id: this.data.groupId,
      })
        .then((response) => {
          const { code, title, message } = response || {};
          if (code === "OK") {
            this.getRules(this.data.groupId);
          }
          this.showMessage(title, message);
          this.close();
        })
        .catch((error) => {
          this.showMessage("Erro", "Não foi possível salvar a regra.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formIsValid() {
      if (!this.level) {
        return false;
      }
      if ((this.level === "user" || this.level === "scale") && !this.scale_id) {
        return false;
      }
      if (this.level === "user" && !this.user_id) {
        return false;
      }
      if (!this.type) {
        return false;
      }
      if (!this.value || this.value <= 0) {
        return false;
      }
      if (!this.action) {
        return false;
      }
      return true;
    },
    resetForm() {
      this.form = {
        level: this.data.level || null,
        type: null,
        value: null,
        action: null,
        scale_id: this.level === "user" ? this.scale_id : null,
      };
      this.$refs.form?.resetValidation?.();
    },
    showMessage(title, message, type = "alert", callback = null) {
      this.$root.$emit("showMessage", { title, message, type, callback });
    },
  },
};
</script>
